import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  Renderer2,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { tap } from 'rxjs/operators';

import { I18nService, TranslatePipe } from '@core/i18n-legacy';

import type { IconName } from '../../../common/icon/icons.type';
import { ScrollableDirective } from '../../../core/overlay/scroll/scrollable.directive';
import { FormFieldComponent } from '../../../forms/form-field/form-field.component';
import { InputComponent } from '../../../forms/input/input.component';
import { DividerComponent } from '../../divider/divider.component';
import { IconComponent } from '../../icon/icon.component';
import type { TooltipPosition } from '../../tooltip/tooltip-inner.component';
import { RtTooltip } from '../../tooltip/tooltip.directive';
import { DropdownHeaderComponent } from '../dropdown-header/dropdown-header.component';
import { DropdownItemComponent } from '../dropdown-item/dropdown-item.component';
import { DropdownComponent, DropdownWidth } from '../dropdown.component';

export type DropdownItem<T = any> = {
  option: string;
  description?: string;
  value: T;
  icon?: IconName;
  badgeTemplate?: TemplateRef<any>;
  disabled?: boolean;
  tooltip?: string;
  tooltipEnabled?: boolean;
  tooltipPosition?: TooltipPosition;
  highlighted?: boolean;
};
export type DropdownGroupedItems<T = any> = {
  items: Array<DropdownItem<T>>;
  title: string;
};

@Component({
  selector: 'rt-dropdown-scrollable',
  templateUrl: './dropdown-scrollable.component.html',
  styleUrls: ['./dropdown-scrollable.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconComponent,
    ScrollableDirective,
    NgTemplateOutlet,
    RtTooltip,
    DividerComponent,
    InputComponent,
    FormFieldComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class DropdownScrollableComponent<T> {
  private readonly renderer = inject(Renderer2);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18n = inject(I18nService);

  public readonly items = input<Array<DropdownItem<T>>>();
  public readonly groupedItems = input<Array<DropdownGroupedItems<T>>>();
  public readonly maxHeight = input<number>(200);
  public readonly width = input<DropdownWidth>(DropdownWidth.Medium);
  public readonly header = input<string>();
  public readonly hasSearch = input(false);
  public readonly searchPlaceholder = input<string>(this.i18n.translate('app.common.search'));

  public selectionChange = output<T>();

  protected searchControl = this.fb.nonNullable.control('');
  protected visibleItems = computed(() => {
    const items = this.items();
    const query = this.query();
    if (!items || !query) return items;
    return items.filter(item => item.option.toLowerCase().includes(query));
  });
  protected visibleGroupedItems = computed(() => {
    const groupedItems = this.groupedItems();
    const query = this.query();
    if (!groupedItems || !query) return groupedItems;
    return <Array<DropdownGroupedItems<T>>>groupedItems
      .map(group => ({
        ...group,
        items: group.items.filter(item => item.option.toLowerCase().includes(query)),
      }))
      .filter(group => group.items.length > 0);
  });

  private scrollWrapEl = viewChild<ElementRef>('scrollWrap');
  private query = signal('');

  protected ngOnInit(): void {
    this.setComponentHeight();
    this.searchControl.valueChanges
      .pipe(
        tap(value => {
          this.query.set(value?.trim()?.toLowerCase() || '');
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private setComponentHeight(): void {
    this.renderer.setStyle(
      this.scrollWrapEl()?.nativeElement,
      'max-height',
      `${this.maxHeight()}px`,
    );
  }
}
